// client/src/pages/secure/RecruitingPage.js — Administration -> Recruiting.
//
//   Applications  the list (newest first — nothing ranks or scores applicants), and one
//                 application: licence lookup links (IDFPR / DBPR, copy the number and paste
//                 it; the portal never scrapes either site), the check staff record, stage,
//                 notes, tasks, interviews, the résumé, and "Start onboarding".
//   Page content  the public /work-with-us sections, FAQ and testimonials, per site, with
//                 the hiring-language check.
//   Settings      accepting applications, page title, the internal staff email notice.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';
import { Card, Empty, ErrorLine, Tabs, Btn, input, fmtDate, fmtDateTime } from '../../components/secure/territory/Shared';

const STAGE_TONE = {
    new: 'bg-indigo-100 text-indigo-800', contacted: 'bg-sky-100 text-sky-800', interview: 'bg-amber-100 text-amber-800',
    offer: 'bg-violet-100 text-violet-800', onboarding: 'bg-emerald-100 text-emerald-800', joined: 'bg-green-200 text-green-900',
    declined: 'bg-slate-200 text-slate-700',
};
const CHECK_LABEL = { active: 'Active', expired: 'Expired / inactive', not_found: 'Not found', other: 'Other' };
const Pill = ({ stage, label }) => <span className={`text-xs px-2 py-0.5 rounded ${STAGE_TONE[stage] || 'bg-slate-100'}`}>{label || stage}</span>;
const todayIso = () => new Date().toLocaleDateString('en-CA');

// ---------------------------------------------------------------------------
const Findings = ({ findings }) => (findings && findings.length ? (
    <ul className="text-xs mt-2 space-y-1">
        {findings.map((f, i) => (
            <li key={i} className={f.severity === 'block' ? 'text-red-700' : 'text-amber-700'}>
                <strong>“{f.term}”</strong> ({f.protected_class}): {f.why} {f.suggestion}
            </li>
        ))}
    </ul>
) : null);

const LicencePanel = ({ app, onSaved }) => {
    const api = useApiClient();
    const [form, setForm] = useState(null);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState('');
    const copy = async (n) => { try { await navigator.clipboard.writeText(n); setCopied(n); } catch { setCopied(''); } };
    const save = async () => {
        try { await api.post(`/api/recruiting/applications/${app.application_id}/licence-checks`, form); setForm(null); setError(''); onSaved(); }
        catch (e) { setError(e.message); }
    };
    return (
        <Card title="Licence verification">
            {app.lookups.length === 0 ? <Empty>No licence number given ({app.licence_status_label}).</Empty> : (
                <ul className="text-sm space-y-2 mb-3">
                    {app.lookups.map((l) => (
                        <li key={l.state} className="flex flex-wrap gap-2 items-center">
                            <span className="font-medium">{l.state}: {l.licence_number}</span>
                            <Btn kind="secondary" onClick={() => copy(l.licence_number)}>{copied === l.licence_number ? 'Copied' : 'Copy number'}</Btn>
                            <a className="text-indigo-600 underline" href={l.url} target="_blank" rel="noopener noreferrer">Open {l.name}</a>
                            <Btn kind="secondary" onClick={() => setForm({ state: l.state, licence_number: l.licence_number, result: 'active', verified_on: todayIso(), expires_on: '', licence_type_found: '', note: '' })}>Record result</Btn>
                            <span className="w-full text-xs text-slate-500">{l.how} The lookup does not accept a number in its address, so paste it.</span>
                        </li>
                    ))}
                </ul>
            )}
            {form && (
                <div className="border rounded p-3 bg-slate-50 grid md:grid-cols-3 gap-2 text-sm mb-3">
                    <label>Result<select className={input} value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })}>
                        {Object.entries(CHECK_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
                    <label>Checked on<input className={input} type="date" max={todayIso()} value={form.verified_on} onChange={(e) => setForm({ ...form, verified_on: e.target.value })} /></label>
                    <label>Expires (as shown)<input className={input} type="date" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} /></label>
                    <label>Licence type shown<input className={input} value={form.licence_type_found} onChange={(e) => setForm({ ...form, licence_type_found: e.target.value })} /></label>
                    <label className="md:col-span-2">Note<input className={input} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
                    <div className="md:col-span-3 flex gap-2"><Btn onClick={save}>Save check</Btn><Btn kind="secondary" onClick={() => setForm(null)}>Cancel</Btn></div>
                    <ErrorLine error={error} />
                </div>
            )}
            {app.checks.length > 0 && (
                <DataList rows={app.checks} rowKey="check_id" csvName="licence-checks" defaultSort={{ key: 'verified_on', dir: 'desc' }}
                    columns={[
                        { key: 'state', label: 'State' },
                        { key: 'licence_number', label: 'Number' },
                        { key: 'result', label: 'Result', value: (c) => `${CHECK_LABEL[c.result] || c.result}${c.note ? ` — ${c.note}` : ''}`,
                            render: (c) => <span className={c.result === 'active' ? 'text-green-700' : 'text-red-700'}>{CHECK_LABEL[c.result]}{c.note ? ` — ${c.note}` : ''}</span> },
                        { key: 'expires_on', label: 'Expires', type: 'date', render: (c) => fmtDate(c.expires_on) },
                        { key: 'verified_on', label: 'Verified by / on', type: 'date', value: (c) => `${c.verified_by || `user #${c.verified_by_user_id}`} · ${fmtDate(c.verified_on)}`, sortValue: (c) => c.verified_on || null },
                    ]} />
            )}
        </Card>
    );
};

const OnboardingHandoff = ({ app, onDone }) => {
    const api = useApiClient();
    const [templates, setTemplates] = useState([]);
    const [f, setF] = useState({ template_id: '', send_welcome_email: false, assigned_email: '', override_reason: '', job_title: '' });
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    useEffect(() => {
        api.get('/api/onboarding/templates').then((t) => {
            const mine = (t || []).filter((x) => x.audience === app.audience);
            setTemplates(mine);
            if (mine.length === 1) setF((p) => ({ ...p, template_id: String(mine[0].template_id) }));
        }).catch((e) => setError(e.message));
    }, [api, app.audience]);
    const go = async () => {
        try { const r = await api.post(`/api/recruiting/applications/${app.application_id}/start-onboarding`, f); setResult(r); setError(''); onDone(); }
        catch (e) { setError(e.message); }
    };
    if (app.onboarding_id || result) {
        const r = result || {};
        return (
            <Card title="Onboarding">
                <p className="text-sm">Onboarding #{app.onboarding_id || r.onboarding_id} is under way{r.linked_existing ? ' (an existing record was linked)' : ''}.
                    {r.forms_issued ? ` ${r.forms_issued} form(s) issued.` : ''} <Link className="text-indigo-600 underline" to="/dashboard/onboarding">Open Onboarding</Link>
                    {app.agent_id ? <> · <Link className="text-indigo-600 underline" to={`/dashboard/agents/${app.agent_id}`}>Licensee record</Link></> : null}
                    {app.employee_id ? <> · <Link className="text-indigo-600 underline" to={`/dashboard/employees/${app.employee_id}`}>Employee record</Link></> : null}
                </p>
                {r.form_problems && r.form_problems.length > 0 && <ul className="text-xs text-amber-700 mt-2">{r.form_problems.map((p) => <li key={p}>{p}</li>)}</ul>}
            </Card>
        );
    }
    if (['declined', 'joined'].includes(app.stage)) return null;
    const needsOverride = app.handoff && !app.handoff.ok && app.handoff.problems.some((p) => /licence check/.test(p));
    return (
        <Card title="Start onboarding">
            <p className="text-xs text-slate-500 mb-2">Creates the {app.audience === 'agent' ? 'licensee record (status Pending — not on the public registry until you activate it)' : 'employee record'} and starts the onboarding checklist from the template, exactly as Onboarding does.</p>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
                <label>Template<select className={input} value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })}>
                    <option value="">Choose…</option>{templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}</select></label>
                <label>Brokerage email to assign (optional)<input className={input} value={f.assigned_email} onChange={(e) => setF({ ...f, assigned_email: e.target.value })} /></label>
                {app.audience === 'employee' && <label>Job title<input className={input} value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} /></label>}
                <label className="flex items-center gap-2 md:col-span-2"><input type="checkbox" checked={f.send_welcome_email} onChange={(e) => setF({ ...f, send_welcome_email: e.target.checked })} />
                    Send Onboarding's welcome email to {app.email || '(no email on file)'}</label>
                {needsOverride && <label className="md:col-span-2">No active licence check recorded — reason to proceed anyway<input className={input} value={f.override_reason} onChange={(e) => setF({ ...f, override_reason: e.target.value })} /></label>}
            </div>
            <ErrorLine error={error} />
            <Btn className="mt-2" onClick={go} disabled={!f.template_id}>Start onboarding</Btn>
        </Card>
    );
};

const ApplicationDetail = ({ id, onChanged }) => {
    const api = useApiClient();
    const user = getCurrentUser();
    const [app, setApp] = useState(null);
    const [error, setError] = useState('');
    const [note, setNote] = useState('');
    const [task, setTask] = useState({ title: '', due_date: '' });
    const [iv, setIv] = useState({ scheduled_at: '', duration_minutes: 30, mode: 'video', location: '' });
    const [declineReason, setDeclineReason] = useState('');
    const load = useCallback(async () => {
        try { setApp(await api.get(`/api/recruiting/applications/${id}`)); setError(''); } catch (e) { setError(e.message); }
    }, [api, id]);
    useEffect(() => { load(); }, [load]);
    const act = async (fn) => { try { await fn(); setError(''); await load(); onChanged(); return true; } catch (e) { setError(e.message); return false; } };
    const downloadResume = async () => {
        const r = await fetch(`/api/recruiting/applications/${id}/resume`, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
        if (!r.ok) { const j = await r.json().catch(() => ({})); setError(j.message || 'Could not download.'); return; }
        const url = URL.createObjectURL(await r.blob());
        const a = document.createElement('a'); a.href = url; a.download = app.resume_original_name || 'resume'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    };
    if (!app) return <Card><ErrorLine error={error} />{!error && <Empty>Loading…</Empty>}</Card>;
    const nextStages = ['new', 'contacted', 'interview', 'offer'].filter((s) => s !== app.stage);
    return (
        <div className="space-y-4">
            <Card title={`${app.first_name} ${app.last_name}`} right={<Pill stage={app.stage} label={app.stage_label} />}>
                <ErrorLine error={error} />
                <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div><dt className="inline text-slate-500">Status: </dt><dd className="inline">{app.licence_status_label}</dd></div>
                    <div><dt className="inline text-slate-500">State(s): </dt><dd className="inline">{app.states || '—'}</dd></div>
                    <div><dt className="inline text-slate-500">Email: </dt><dd className="inline">{app.email || '—'}</dd></div>
                    <div><dt className="inline text-slate-500">Phone: </dt><dd className="inline">{app.phone || '—'}</dd></div>
                    <div><dt className="inline text-slate-500">Current brokerage: </dt><dd className="inline">{app.current_brokerage || '—'}</dd></div>
                    <div><dt className="inline text-slate-500">Years licensed / deals (12 mo): </dt><dd className="inline">{app.years_licensed ?? '—'} / {app.transactions_last_12mo ?? '—'}</dd></div>
                    <div className="md:col-span-2"><dt className="inline text-slate-500">Specialties: </dt><dd className="inline">{app.specialties || '—'}</dd></div>
                    <div><dt className="inline text-slate-500">Heard about us: </dt><dd className="inline">{app.heard_about || '—'}</dd></div>
                    <div><dt className="inline text-slate-500">Applied: </dt><dd className="inline">{fmtDateTime(app.created_at)} · site {app.site}</dd></div>
                    <div className="md:col-span-2"><dt className="inline text-slate-500">Contact consent: </dt><dd className="inline">{app.consent_contact ? 'Given (see timeline)' : 'None — contact about the application only; no marketing.'}</dd></div>
                </dl>
                {app.message && <p className="text-sm mt-2 whitespace-pre-line border-l-2 pl-3 text-slate-700">{app.message}</p>}
                <div className="mt-3 flex flex-wrap gap-2 items-center text-sm">
                    {app.has_resume ? <Btn kind="secondary" onClick={downloadResume}>Download résumé</Btn>
                        : <span className="text-slate-500">{app.resume_deleted_at ? `Résumé deleted ${fmtDate(app.resume_deleted_at)}: ${app.resume_deleted_reason}` : 'No résumé'}</span>}
                    {app.has_resume && user?.isManagingBroker && (
                        <Btn kind="danger" onClick={() => {
                            const reason = window.prompt('Why is the résumé being deleted? (e.g. "applicant asked by email on 3 Oct")');
                            if (reason) act(() => api.delete(`/api/recruiting/applications/${id}/resume`, { reason }));
                        }}>Delete résumé on request</Btn>
                    )}
                </div>
            </Card>

            <Card title="Stage">
                <div className="flex flex-wrap gap-2 items-center">
                    {nextStages.map((s) => <Btn key={s} kind="secondary" onClick={() => act(() => api.put(`/api/recruiting/applications/${id}/stage`, { stage: s }))}>Move to {s}</Btn>)}
                    {app.stage === 'onboarding' && <Btn onClick={() => act(() => api.put(`/api/recruiting/applications/${id}/stage`, { stage: 'joined' }))}>Mark joined</Btn>}
                </div>
                {!['declined', 'joined'].includes(app.stage) && (
                    <div className="flex gap-2 mt-3">
                        <input className={input} placeholder="Job-related reason for declining (kept on the record)" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
                        <Btn kind="danger" disabled={!declineReason.trim()} onClick={async () => { if (await act(() => api.put(`/api/recruiting/applications/${id}/stage`, { stage: 'declined', reason: declineReason }))) setDeclineReason(''); }}>Decline</Btn>
                    </div>
                )}
                {app.stage === 'declined' && <p className="text-sm mt-2 text-slate-600">Declined: {app.decline_reason}</p>}
            </Card>

            <LicencePanel app={app} onSaved={load} />
            <OnboardingHandoff app={app} onDone={() => { load(); onChanged(); }} />

            <Card title="Interviews">
                {app.interviews.length === 0 ? <Empty>None scheduled.</Empty> : (
                    <ul className="text-sm space-y-1 mb-3">{app.interviews.map((i) => (
                        <li key={i.interview_id} className="flex flex-wrap gap-2 items-center">
                            <span>{fmtDateTime(i.scheduled_at)} · {i.mode.replace('_', ' ')} · {i.duration_minutes} min{i.location ? ` · ${i.location}` : ''}</span>
                            <span className="text-xs px-2 rounded bg-slate-100">{i.interview_status}</span>
                            {i.interview_status === 'scheduled' && ['held', 'no_show', 'cancelled'].map((s) => (
                                <Btn key={s} kind="secondary" onClick={() => act(() => api.put(`/api/recruiting/interviews/${i.interview_id}`, { interview_status: s }))}>{s.replace('_', ' ')}</Btn>
                            ))}
                        </li>
                    ))}</ul>
                )}
                <div className="grid md:grid-cols-4 gap-2 text-sm">
                    <input className={input} type="datetime-local" value={iv.scheduled_at} onChange={(e) => setIv({ ...iv, scheduled_at: e.target.value })} />
                    <select className={input} value={iv.mode} onChange={(e) => setIv({ ...iv, mode: e.target.value })}><option value="video">Video</option><option value="phone">Phone</option><option value="in_person">In person</option></select>
                    <input className={input} placeholder="Link / place" value={iv.location} onChange={(e) => setIv({ ...iv, location: e.target.value })} />
                    <Btn disabled={!iv.scheduled_at} onClick={async () => { if (await act(() => api.post(`/api/recruiting/applications/${id}/interviews`, iv))) setIv({ ...iv, scheduled_at: '', location: '' }); }}>Schedule</Btn>
                </div>
                <p className="text-xs text-slate-500 mt-1">Adds a task for the interviewer. Invitations are not sent from here — arrange the time with the applicant directly.</p>
            </Card>

            <Card title="Tasks">
                {app.tasks.length === 0 ? <Empty>No tasks.</Empty> : (
                    <ul className="text-sm space-y-1 mb-3">{app.tasks.map((t) => (
                        <li key={t.task_id} className="flex gap-2 items-center">
                            <input type="checkbox" checked={t.status === 'Completed'} onChange={(e) => act(() => api.put(`/api/recruiting/applications/${id}/tasks/${t.task_id}`, { status: e.target.checked ? 'Completed' : 'Open' }))} />
                            <span className={t.status !== 'Open' ? 'line-through text-slate-400' : ''}>{t.title}</span>
                            <span className="text-xs text-slate-500">{t.due_date ? `due ${fmtDate(t.due_date)}` : ''}</span>
                        </li>
                    ))}</ul>
                )}
                <div className="flex gap-2">
                    <input className={input} placeholder="New task" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} />
                    <input className={`${input} max-w-[10rem]`} type="date" value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} />
                    <Btn disabled={!task.title.trim()} onClick={async () => { if (await act(() => api.post(`/api/recruiting/applications/${id}/tasks`, task))) setTask({ title: '', due_date: '' }); }}>Add</Btn>
                </div>
            </Card>

            <Card title="Notes and timeline">
                <div className="flex gap-2 mb-3">
                    <textarea className={input} rows="2" placeholder="Add a note (job-related facts only)" value={note} onChange={(e) => setNote(e.target.value)} />
                    <Btn disabled={!note.trim()} onClick={async () => { if (await act(() => api.post(`/api/recruiting/applications/${id}/notes`, { body: note }))) setNote(''); }}>Add</Btn>
                </div>
                <ul className="text-sm space-y-2">{app.events.map((e) => (
                    <li key={e.event_id} className="border-l-2 pl-3">
                        <span className="text-xs text-slate-500">{fmtDateTime(e.created_at)} · {e.by_user || 'system'} · {e.event_kind}{e.to_stage ? ` → ${e.to_stage}` : ''}</span>
                        {e.body && <p className="whitespace-pre-line">{e.body}</p>}
                    </li>
                ))}</ul>
            </Card>
        </div>
    );
};

const ApplicationsTab = () => {
    const api = useApiClient();
    const [params, setParams] = useSearchParams();
    const selected = params.get('id');
    const [stage, setStage] = useState('');
    const [q, setQ] = useState('');
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        try {
            const qs = new URLSearchParams({ ...(stage ? { stage } : {}), ...(q ? { q } : {}) }).toString();
            const [list, sum] = await Promise.all([api.get(`/api/recruiting/applications?${qs}`), api.get('/api/recruiting/summary')]);
            setRows(list); setSummary(sum); setError('');
        } catch (e) { setError(e.message); }
    }, [api, stage, q]);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 space-y-3">
                <Card>
                    <div className="flex flex-wrap gap-1 mb-3">
                        <button type="button" onClick={() => setStage('')} className={`text-xs px-2 py-1 rounded ${!stage ? 'bg-slate-800 text-white' : 'bg-slate-100'}`}>All</button>
                        {summary && summary.stages.map((s) => (
                            <button type="button" key={s.value} onClick={() => setStage(s.value)} className={`text-xs px-2 py-1 rounded ${stage === s.value ? 'bg-slate-800 text-white' : STAGE_TONE[s.value]}`}>
                                {s.label} {summary.counts[s.value] || 0}
                            </button>
                        ))}
                    </div>
                    <input className={input} placeholder="Search name, email or licence number" value={q} onChange={(e) => setQ(e.target.value)} />
                    <ErrorLine error={error} />
                    <p className="text-xs text-slate-500 mt-2">Newest first. Applicants are listed, never scored or ranked.</p>
                </Card>
                <Card>
                    <p className="text-xs text-slate-500 mb-2">The box below searches and sorts the {rows.length} application{rows.length === 1 ? '' : 's'} loaded (up to 500); the search above asks the server, including licence numbers.</p>
                    <DataList rows={rows} rowKey="application_id" csvName="recruiting-applications" defaultSort={{ key: 'created_at', dir: 'desc' }}
                        onRowClick={(r) => setParams({ id: String(r.application_id) })}
                        empty={`No applications${stage ? ' at this stage' : ''}.`}
                        filters={[{ key: 'licence_status_label', label: 'Licence' }, { key: 'states', label: 'States' }]}
                        columns={[
                            { key: 'name', label: 'Name', value: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                                render: (r) => <span className={`font-medium ${String(r.application_id) === selected ? 'text-indigo-700' : ''}`}>{r.first_name} {r.last_name}</span> },
                            { key: 'stage_label', label: 'Stage', render: (r) => <Pill stage={r.stage} label={r.stage_label} /> },
                            { key: 'licence_status_label', label: 'Licence' },
                            { key: 'states', label: 'States' },
                            { key: 'created_at', label: 'Applied', type: 'date', render: (r) => fmtDate(r.created_at) },
                            { key: 'last_check', label: 'Licence check', value: (r) => (r.last_check ? CHECK_LABEL[r.last_check] : '') },
                            { key: 'next_interview', label: 'Interview', type: 'date', render: (r) => (r.next_interview ? fmtDateTime(r.next_interview) : '') },
                            { key: 'email', label: 'Email' },
                        ]} />
                </Card>
            </div>
            <div className="lg:col-span-3">
                {selected ? <ApplicationDetail key={selected} id={selected} onChanged={load} /> : <Card><Empty>Choose an application.</Empty></Card>}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
const BlockEditor = ({ block, onSaved }) => {
    const api = useApiClient();
    const [b, setB] = useState(block);
    const [msg, setMsg] = useState('');
    const [findings, setFindings] = useState([]);
    const save = async () => {
        try { const r = await api.put(`/api/recruiting/content/${b.block_id}`, b); setFindings(r.warnings || []); setMsg('Saved.'); onSaved(); }
        catch (e) { setMsg(e.message); setFindings((e.payload && e.payload.findings) || []); }
    };
    const remove = async () => { if (window.confirm('Delete this entry?')) { try { await api.delete(`/api/recruiting/content/${b.block_id}`); onSaved(); } catch (e) { setMsg(e.message); } } };
    const placeholder = { commission_plans: 'Your actual commission plans (splits, caps). Left blank, this section is not shown.',
        fees: 'Your actual fees (monthly, per-transaction, E&O). Left blank, this section is not shown.',
        support: 'What support licensees get (managing-broker availability, transaction coordination…). Left blank, it is not shown.' }[b.block_key] || '';
    return (
        <div className="border rounded p-3 space-y-2 text-sm">
            <div className="flex gap-2 items-center">
                <input className={input} value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} placeholder={b.kind === 'faq' ? 'Question' : b.kind === 'testimonial' ? 'Name' : 'Heading'} />
                <label className="flex items-center gap-1 whitespace-nowrap"><input type="checkbox" checked={!!Number(b.is_published)} disabled={b.block_key === 'eeo'} onChange={(e) => setB({ ...b, is_published: e.target.checked ? 1 : 0 })} />Shown</label>
                <input className={`${input} max-w-[5rem]`} type="number" title="Order" value={b.sort_order} onChange={(e) => setB({ ...b, sort_order: e.target.value })} />
            </div>
            <textarea className={input} rows={b.kind === 'section' ? 4 : 3} value={b.body || ''} placeholder={placeholder} onChange={(e) => setB({ ...b, body: e.target.value })} />
            {b.kind === 'testimonial' && <input className={input} placeholder="Attribution as they agreed to be named" value={b.attribution || ''} onChange={(e) => setB({ ...b, attribution: e.target.value })} />}
            <div className="flex gap-2 items-center"><Btn onClick={save}>Save</Btn>{b.kind !== 'section' && <Btn kind="danger" onClick={remove}>Delete</Btn>}
                <span className="text-xs text-slate-500">{b.block_key}{b.block_key === 'training' ? ' · links to /training' : ''}</span><span className="text-xs">{msg}</span></div>
            <Findings findings={findings} />
        </div>
    );
};

const ContentTab = () => {
    const api = useApiClient();
    const [site, setSite] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [draft, setDraft] = useState({ kind: 'faq', title: '', body: '', attribution: '', permission_confirmed: false });
    const [findings, setFindings] = useState([]);
    const load = useCallback(async () => {
        try { setData(await api.get(`/api/recruiting/content${site ? `?site=${site}` : ''}`)); setError(''); } catch (e) { setError(e.message); }
    }, [api, site]);
    useEffect(() => { load(); }, [load]);
    const add = async () => {
        try { const r = await api.post('/api/recruiting/content', { ...draft, site: data.site }); setFindings(r.warnings || []);
              setDraft({ kind: draft.kind, title: '', body: '', attribution: '', permission_confirmed: false }); load(); }
        catch (e) { setError(e.message); setFindings((e.payload && e.payload.findings) || []); }
    };
    if (!data) return <Card><ErrorLine error={error} /><Empty>Loading…</Empty></Card>;
    const of = (k) => data.blocks.filter((b) => b.kind === k);
    return (
        <div className="space-y-4">
            <Card title="Page content" right={
                <select className="p-2 border rounded text-sm" value={data.site} onChange={(e) => setSite(e.target.value)}>
                    {data.sites.map((s) => <option key={s.value} value={s.value}>{s.label} site</option>)}
                </select>}>
                <p className="text-sm text-slate-600">What <a className="text-indigo-600 underline" href="/work-with-us" target="_blank" rel="noopener noreferrer">/work-with-us</a> shows on the {data.site} site. Blank sections are hidden — enter the brokerage's real terms; nothing is filled in for you. Every save runs the hiring-language check (age, pay history, criminal history, national origin, disability, AI-screening claims).</p>
                <ErrorLine error={error} />
            </Card>
            <Card title="Sections"><div className="space-y-3">{of('section').map((b) => <BlockEditor key={b.block_id} block={b} onSaved={load} />)}</div></Card>
            <Card title="FAQ"><div className="space-y-3">{of('faq').map((b) => <BlockEditor key={b.block_id} block={b} onSaved={load} />)}</div></Card>
            <Card title="Testimonials">
                {of('testimonial').length === 0 ? <Empty>None. Testimonials appear only when staff enter them here, with the person's permission.</Empty>
                    : <div className="space-y-3">{of('testimonial').map((b) => <BlockEditor key={b.block_id} block={b} onSaved={load} />)}</div>}
            </Card>
            <Card title="Add an FAQ entry or testimonial">
                <div className="space-y-2 text-sm">
                    <select className={input} value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}><option value="faq">FAQ entry</option><option value="testimonial">Testimonial</option></select>
                    <input className={input} placeholder={draft.kind === 'faq' ? 'Question' : 'Name'} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                    <textarea className={input} rows="3" placeholder={draft.kind === 'faq' ? 'Answer' : 'Their words, as they gave them'} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
                    {draft.kind === 'testimonial' && <>
                        <input className={input} placeholder="Attribution (e.g. Jane D., broker since 2019)" value={draft.attribution} onChange={(e) => setDraft({ ...draft, attribution: e.target.value })} />
                        <label className="flex items-center gap-2"><input type="checkbox" checked={draft.permission_confirmed} onChange={(e) => setDraft({ ...draft, permission_confirmed: e.target.checked })} />They agreed to this being published under that name.</label>
                    </>}
                    <Btn onClick={add} disabled={!draft.title.trim() || !draft.body.trim()}>Add</Btn>
                    <Findings findings={findings} />
                </div>
            </Card>
        </div>
    );
};

const SettingsTab = () => {
    const api = useApiClient();
    const user = getCurrentUser();
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState('');
    useEffect(() => { api.get('/api/recruiting/settings').then(setS).catch((e) => setMsg(e.message)); }, [api]);
    if (!s) return <Card><Empty>{msg || 'Loading…'}</Empty></Card>;
    const mb = !!user?.isManagingBroker;
    const save = async () => { try { const r = await api.put('/api/recruiting/settings', s); setS({ ...s, ...r }); setMsg('Saved.'); } catch (e) { setMsg(e.message); } };
    return (
        <Card title={`Settings — ${s.site} site`}>
            <div className="space-y-3 text-sm max-w-xl">
                <label className="flex items-center gap-2"><input type="checkbox" disabled={!mb} checked={!!Number(s.accepting_applications)} onChange={(e) => setS({ ...s, accepting_applications: e.target.checked ? 1 : 0 })} />Accepting applications on the website</label>
                <label className="block">Page title<input className={input} disabled={!mb} value={s.page_title || ''} onChange={(e) => setS({ ...s, page_title: e.target.value })} /></label>
                <label className="flex items-center gap-2"><input type="checkbox" disabled={!mb} checked={!!Number(s.notify_staff_email)} onChange={(e) => setS({ ...s, notify_staff_email: e.target.checked ? 1 : 0 })} />Email staff an internal notice when an application arrives</label>
                <label className="block">Staff addresses (comma separated; {s.internal_domains.join(', ') || 'brokerage domain'} only)<input className={input} disabled={!mb} value={s.notify_email_to || ''} onChange={(e) => setS({ ...s, notify_email_to: e.target.value })} /></label>
                <p className="text-xs text-slate-500">The notice names the applicant and their licence status and links here — no résumé, no message. Nothing is ever sent to the applicant from this module. {s.mail_configured ? '' : 'Email is not configured on this server, so no notice would go out yet.'} Whatever this setting, every application makes a task for the managing broker and shows on Portal Home.</p>
                {mb ? <Btn onClick={save}>Save</Btn> : <p className="text-xs">Only the managing broker changes these.</p>}
                <p className="text-xs">{msg}</p>
            </div>
        </Card>
    );
};

const RecruitingPage = () => {
    const [tab, setTab] = useState('apps');
    return (
        <div className="p-4 md:p-6">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">Recruiting</h1>
            <p className="text-sm text-slate-600 mb-4">Applications from the public <a className="text-indigo-600 underline" href="/work-with-us" target="_blank" rel="noopener noreferrer">Work With Us</a> page. Kept on the 5-year records register (Records Retention).</p>
            <Tabs tabs={[['apps', 'Applications'], ['content', 'Page content'], ['settings', 'Settings']]} value={tab} onChange={setTab} />
            {tab === 'apps' && <ApplicationsTab />}
            {tab === 'content' && <ContentTab />}
            {tab === 'settings' && <SettingsTab />}
        </div>
    );
};

export default RecruitingPage;
